import React, { useCallback } from "react";
import { AlertCircleIcon } from "@icons";
import { ImporterDocumentStatusMessage } from "@components/importer/ImporterDocumentStatusMessage";
import drawerStyles from "../AccrualImporterDrawer.module.scss";
import { AccrualImporterHeaderDetails } from "./AccrualImporterHeaderDetails";
import { AccrualImporterLineItemsGrid } from "./AccrualImporterLineItemsGrid";
import { removeInvoiceGroup } from "./groupHelpers";
import { getInvoiceGroupWarnings } from "./invoiceGroupWarnings";
import { AccrualImporterGroupedSectionsProps } from "./groupedSections.types";
import styles from "./AccrualImporterGroupedSections.module.scss";

export const AccrualImporterGroupedSections: React.FC<
    AccrualImporterGroupedSectionsProps
> = ({
    mode,
    setRows,
    addLineItemToGroup,
    removeRow,
    addNewInvoice,
    columns,
    groups,
}) => {
    const accrualNoun = mode === "bill" ? "bill" : "invoice";
    const canRemoveGroup = groups.length > 1;
    const removeGroupLabel = `Remove ${accrualNoun}`;

    const handleRemoveGroup = useCallback(
        (groupKey: string) => {
            setRows((previousRows) =>
                removeInvoiceGroup({ rows: previousRows, groupKey }),
            );
        },
        [setRows],
    );

    return (
        <div className={styles.scrollContainer}>
            {groups.map((group) => {
                const warnings =
                    group.warnings ?? getInvoiceGroupWarnings({ group, mode });

                return (
                    <section key={group.groupKey} className={styles.section}>
                        <div className={styles.sectionContainer}>
                            <AccrualImporterHeaderDetails
                                mode={mode}
                                row={group.headerRow}
                                columns={columns}
                                setRows={setRows}
                                globalRowIndex={group.headerRowIndex}
                                canRemoveGroup={canRemoveGroup}
                                onRemoveGroup={() =>
                                    handleRemoveGroup(group.groupKey)
                                }
                                removeGroupLabel={removeGroupLabel}
                            />
                            <div className={styles.lineItemsSection}>
                                <AccrualImporterLineItemsGrid
                                    lineRows={group.lineRows}
                                    globalRowIndices={group.lineRowIndices}
                                    columns={columns}
                                    setRows={setRows}
                                    addLineItemToGroup={addLineItemToGroup}
                                    removeRow={removeRow}
                                    className={
                                        drawerStyles.accrualImporterAmountGrid
                                    }
                                />
                            </div>
                            {warnings.length > 0 ? (
                                <div className={styles.sectionWarnings}>
                                    {warnings.map((warning) => (
                                        <ImporterDocumentStatusMessage
                                            key={warning}
                                            icon={
                                                <AlertCircleIcon className="icon-color-yellow-600" />
                                            }
                                            message={warning}
                                            className="text-yellow-600"
                                        />
                                    ))}
                                </div>
                            ) : null}
                        </div>
                    </section>
                );
            })}
            <div className={styles.addSectionRow}>
                <button
                    type="button"
                    className={drawerStyles.addLineButton}
                    onClick={addNewInvoice}
                >
                    + add {accrualNoun}
                </button>
            </div>
        </div>
    );
};
